import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setToken, getToken } from "../lib/adminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) {
      // Already logged in — go straight to dashboard
      nav("/admin/dashboard", { replace: true });
    }
  }, [nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          typeof data.detail === "string"
            ? data.detail
            : "Login failed. Please try again.";
        toast.error(msg);
        return;
      }
      const data = await res.json();
      setToken(data.token);
      toast.success(`Welcome back, ${data.username}.`);
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-[100svh] bg-[#0F2A22] text-[#FDFBF7] flex items-center justify-center px-6 py-16 relative overflow-hidden"
    >
      {/* Decorative rings */}
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full border border-[#D4AF37]/15 pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full border border-[#D4AF37]/10 pointer-events-none" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-block mb-10 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] hover:text-[#FDFBF7] transition-colors"
          data-testid="admin-back-home"
        >
          ← Back to website
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-full bg-[#1B4D3E] flex items-center justify-center">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
              Caretaker Access
            </div>
            <div className="font-display text-2xl text-[#FDFBF7]">
              Hastkesar Panjrapole
            </div>
          </div>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] mb-3">
          Update the family<br />
          <span className="italic text-[#D4AF37]">today's census.</span>
        </h1>
        <p className="text-[#FDFBF7]/65 leading-relaxed mb-10">
          Sign in with your caretaker credentials to update the daily count of
          every animal under our roof.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/60">
              Username
            </span>
            <input
              data-testid="admin-username-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              className="mt-2 w-full bg-transparent border-b border-[#FDFBF7]/30 focus:border-[#D4AF37] outline-none py-3 text-[#FDFBF7] text-lg placeholder:text-[#FDFBF7]/30 transition-colors"
              placeholder="Hastkesar"
            />
          </label>

          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/60">
              Password
            </span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-2 w-full bg-transparent border-b border-[#FDFBF7]/30 focus:border-[#D4AF37] outline-none py-3 text-[#FDFBF7] text-lg placeholder:text-[#FDFBF7]/30 transition-colors"
              placeholder="••••••••••"
            />
          </label>

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="group mt-4 inline-flex items-center justify-center gap-3 w-full bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-60 text-[#1A2E24] px-6 py-4 rounded-full text-sm font-semibold tracking-wide transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        <div className="mt-10 pt-6 border-t border-[#FDFBF7]/10 text-xs text-[#FDFBF7]/45 leading-relaxed">
          This page is private. Access is restricted to the Bokadia family and
          authorised caretakers only.
        </div>
      </div>
    </div>
  );
}
